import TechHero from "../components/TechHero";
import useSEO from "../hooks/useSEO";

export default function StudentProjects() {
  useSEO(
    "Final Year Student Projects | ZS NEXATECH",
    "Explore final-year student project development services including complete source code, documentation, PPTs, and demo-ready delivery in your required tech stack.",
    "student projects, final year project, college project development, project documentation, demo-ready projects",
    "https://www.zsnexatech.com/student-projects"
  );

  return (
    <div className="space-y-12">
      <TechHero
        kicker="Student innovation lab"
        title="Final‑year projects built to impress"
        titleAs="h1"
        subtitle="We build college‑ready websites, web apps, and software in your required tech stack—complete with reports, PPTs, and demo support."
        ctaText="Request a student project"
        ctaLink="/contact"
      />

      <section className="grid gap-6 md:grid-cols-3">
        {[
          {
            title: "Stack‑matched builds",
            desc: "React, Node, Spring Boot, Python, PHP, MySQL, MongoDB, or any college‑approved stack.",
            meta: "Your stack",
            image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=600&fit=crop&q=80",
          },
          {
            title: "Complete documentation",
            desc: "Project report templates, SRS, ER diagrams, PPT, and setup steps included.",
            meta: "Docs ready",
            image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop&q=80",
          },
          {
            title: "Demo‑ready delivery",
            desc: "We help with hosting, live demo setup, and last‑minute fixes before submission.",
            meta: "Viva ready",
            image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=600&fit=crop&q=80",
          },
        ].map((item) => (
          <div
            key={item.title}
            className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg overflow-hidden hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-500 group"
          >
            <div className="relative h-48 overflow-hidden">
              <img 
                src={item.image} 
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/40 via-rose-500/30 to-purple-600/40 group-hover:from-amber-400/50 group-hover:via-rose-400/40 group-hover:to-purple-500/50 transition-all duration-500"></div>
              <div className="absolute top-3 left-3 text-xs font-semibold text-white bg-red-500/90 backdrop-blur-sm px-3 py-1.5 rounded-full transform group-hover:scale-110 transition-transform duration-300">{item.meta}</div>
            </div>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm text-gray-100">{item.desc}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white">⚙️ Choose your tech stack</h2>
          <p className="mt-2 text-gray-100">Popular stacks we deliver for final‑year submissions.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {[
            "React",
            "Next.js",
            "Node.js",
            "Express",
            "Spring Boot",
            "Java",
            "Python",
            "Django",
            "PHP",
            "MySQL",
            "PostgreSQL",
            "MongoDB",
          ].map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-white/30 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white/90 backdrop-blur-md"
            >
              {chip}
            </span>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-500 group relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-amber-500/0 to-rose-600/0 group-hover:from-amber-500/10 group-hover:to-rose-600/10 transition-all duration-500"></div>
          <div className="relative z-10">
            <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">📦</div>
            <h2 className="text-2xl font-bold text-white">📦 What you get</h2>
            <ul className="mt-4 space-y-2 text-sm text-gray-100">
              <li className="flex items-start"><span className="text-amber-400 mr-2 mt-0.5">•</span><span>Complete source code with setup instructions</span></li>
              <li className="flex items-start"><span className="text-amber-400 mr-2 mt-0.5">•</span><span>Project report templates + PPT deck</span></li>
              <li className="flex items-start"><span className="text-amber-400 mr-2 mt-0.5">•</span><span>ER diagrams + architecture notes</span></li>
              <li className="flex items-start"><span className="text-amber-400 mr-2 mt-0.5">•</span><span>Deployment guidance and demo checklist</span></li>
            </ul>
          </div>
        </div>
        <div className="card rounded-2xl border border-white/30 bg-white/10 backdrop-blur-lg p-6 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 hover:bg-white/20 transition-all duration-500 group relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none bg-linear-to-br from-purple-500/0 to-pink-600/0 group-hover:from-purple-500/10 group-hover:to-pink-600/10 transition-all duration-500"></div>
          <div className="relative z-10">
            <div className="text-4xl mb-3 transform group-hover:scale-125 group-hover:rotate-6 transition-all duration-500">🧭</div>
            <h2 className="text-2xl font-bold text-white">🧭 Fast timeline</h2>
          <p className="mt-2 text-sm text-gray-100">
            Typical delivery in 5‑15 days depending on complexity. We keep it simple and clear.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {["Idea selection", "Requirement freeze", "Build & test", "Docs + demo"].map((step) => (
              <div
                key={step}
                className="card rounded-xl border border-white/30 bg-white/10 backdrop-blur-md px-4 py-3 text-sm text-white/90 hover:bg-white/20 hover:border-white/40 hover:scale-105 transition-all duration-300"
              >
                ✓ {step}
              </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      <section className="card rounded-3xl border border-white/30 bg-white/10 backdrop-blur-lg p-8 hover:shadow-2xl hover:bg-white/20 transition-all duration-300">
        <h2 className="text-3xl font-bold text-white">🚀 Ready to start your project?</h2>
        <p className="mt-2 max-w-2xl text-gray-100">
          Share your project idea or college requirements. We’ll recommend the best stack, timeline, and documentation plan.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <a href="/contact" className="btn-gold">Start now</a>
          <a className="link-gold" href="/contact">Ask for a demo plan</a>
        </div>
      </section>
    </div>
  );
}
